/**
 * `VisibilityMenu` — the toolbar "Columns" control: a menu of per-column
 * checkboxes wired to the engine's `getIsVisible()` / `toggleVisibility()`. Thin
 * by design — it reads and writes the SAME `table` visibility state the preset
 * threads, so it and the `table.getColumn(id)?.toggleVisibility()` escape hatch
 * share one source of truth. Lists only `getCanHide()` columns, so an
 * `enableHiding: false` selection/actions column stays always-visible.
 */
import type { RowData, Table, VisibilityState } from '@tanstack/react-table'

import { Button } from '@gto/ui/button'
import { ViewColumnsIcon } from '@gto/ui/icons/solid'
import { Menu } from '@gto/ui/menu'

export type VisibilityMenuProps<TData extends RowData> = {
  table: Table<TData>
  /**
   * The engine's visibility state, passed in rather than read off `table`.
   *
   * Load-bearing under the React Compiler, not a convenience. `table` is
   * mutated in place, so its identity NEVER changes — with only `table` and
   * `getColumnLabel` (both stable) as inputs, the compiler has no reason to
   * re-render this subtree and every `getIsVisible()` read freezes at the value
   * it had when the menu first rendered. The grid keeps updating, because it
   * derives through hooks the compiler can see, so the symptom is a menu whose
   * checkboxes disagree with the columns actually on screen.
   */
  columnVisibility: VisibilityState
  /** Trigger label. Defaults to `Columns`; pass a translated node from the app. */
  label?: React.ReactNode
  /**
   * Resolves a column's checkbox label. Defaults to the column `id` — a resolver
   * avoids the menu reaching into `columnDef.header`, which may be a render fn.
   */
  getColumnLabel?: (columnId: string) => React.ReactNode
}

/**
 * Plain generic function, not `FC`: `Table` is invariant in `TData`, so a
 * `<TData,>` component can't be assigned to a non-generic `FC` (this keeps
 * call-site row-type inference).
 */
export const VisibilityMenu = <TData extends RowData>({
  table,
  columnVisibility,
  label = 'Columns',
  getColumnLabel = (columnId) => columnId,
}: VisibilityMenuProps<TData>) => {
  const hideableColumns = table.getAllLeafColumns().filter((column) => column.getCanHide())

  return (
    <Menu.Root>
      <Menu.Trigger
        render={
          <Button
            color="secondary"
            startIcon={<ViewColumnsIcon className="size-4" />}
            variant="outlined"
          >
            {label}
          </Button>
        }
      />
      <Menu.Portal>
        <Menu.Positioner align="end">
          <Menu.Popup className="min-w-[200px]">
            <Menu.Group>
              <Menu.GroupLabel>{label}</Menu.GroupLabel>
              {hideableColumns.map((column) => (
                <Menu.CheckboxItem
                  key={column.id}
                  // Stay open across toggles for the multi-toggle UX.
                  // From the prop, so the value the compiler tracks is the one
                  // rendered. `column.getIsVisible()` is the same number read
                  // off an object React cannot observe changing.
                  checked={columnVisibility[column.id] ?? true}
                  closeOnClick={false}
                  data-column-id={column.id}
                  onCheckedChange={(checked) => column.toggleVisibility(checked)}
                >
                  {getColumnLabel(column.id)}
                </Menu.CheckboxItem>
              ))}
            </Menu.Group>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}
